import type { AIModel, DirectMessage } from '@volt/shared';
import { AUTO_MODEL_CHOICE_ID, availableChatModels, resolveAutoModelId } from '../lib/modelPicker.js';

export interface DirectChatPreferences {
  enabled: boolean;
  modelChoice: string;
}

export function resolveDirectChatModelId(models: AIModel[], choice: string): string | undefined {
  const supported = availableChatModels(models).filter((model) => model.modality === 'text');
  return supported.find((model) => model.id === choice)?.id ?? resolveAutoModelId(supported);
}

export function readDirectChatPreferences(userId: string): DirectChatPreferences {
  try {
    const value: unknown = JSON.parse(
      localStorage.getItem(`volt:direct-chat-ai:${userId}`) ?? 'null',
    );
    if (value && typeof value === 'object') {
      const preferences = value as Partial<DirectChatPreferences>;
      return {
        enabled: preferences.enabled === true,
        modelChoice:
          typeof preferences.modelChoice === 'string' && preferences.modelChoice
            ? preferences.modelChoice
            : AUTO_MODEL_CHOICE_ID,
      };
    }
  } catch {
    // Storage may be unavailable in private browsing.
  }
  return { enabled: false, modelChoice: AUTO_MODEL_CHOICE_ID };
}

export function writeDirectChatPreferences(userId: string, preferences: DirectChatPreferences) {
  try {
    localStorage.setItem(`volt:direct-chat-ai:${userId}`, JSON.stringify(preferences));
  } catch {
    // The current session still works when storage is unavailable.
  }
}

export function isAiDirectMessage(message: DirectMessage): boolean {
  return Boolean(
    message.ai ||
    message.forwardedFrom?.kind === 'ai' ||
    message.forwardedFrom?.sourceRole === 'assistant',
  );
}

export function canEditDirectMessage(message: DirectMessage, userId: string): boolean {
  return message.senderId === userId && !isAiDirectMessage(message);
}

export function compareDirectMessages(a: DirectMessage, b: DirectMessage): number {
  return (
    a.createdAt.localeCompare(b.createdAt) ||
    (a.batchId && a.batchId === b.batchId ? (a.batchIndex ?? 0) - (b.batchIndex ?? 0) : 0) ||
    a.id.localeCompare(b.id)
  );
}

export function orderedForwardIds(messages: DirectMessage[], selectedIds: string[]): string[] {
  const selected = new Set(selectedIds);
  return [...messages]
    .sort(compareDirectMessages)
    .filter((message) => selected.has(message.id))
    .map((message) => message.id);
}

export function toggleDirectMessageSelection(ids: string[], id: string): string[] {
  if (ids.includes(id)) return ids.filter((item) => item !== id);
  return ids.length < 100 ? [...ids, id] : ids;
}

export function groupDirectMessageBatches(messages: DirectMessage[]): DirectMessage[][] {
  const batches: DirectMessage[][] = [];
  for (const message of messages) {
    const previous = batches[batches.length - 1];
    if (message.batchId && previous?.[0]?.batchId === message.batchId) previous.push(message);
    else batches.push([message]);
  }
  return batches;
}
